import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { getLscLeague, listLscLeagues, LscApiError } from '../api';
import {
  getEffectiveLeagueRole,
  isLeagueCommissioner,
  isLeagueOperator,
  isLeagueOwner,
  type EffectiveLeagueRole,
} from '../leagueRoles';
import { resolveActiveLeagueId } from '../resolveActiveLeagueId';
import type { LscLeague, LscMember } from '../types';

interface LeagueContextValue {
  league: LscLeague | null;
  leagues: LscLeague[];
  membership: LscMember | null;
  effectiveMembership: LscMember | null;
  effectiveRole: EffectiveLeagueRole;
  viewerIsCommissioner: boolean;
  isCommissioner: boolean;
  isOwner: boolean;
  isOperator: boolean;
  isMember: boolean;
  loading: boolean;
  error: string | null;
  refreshLeague: () => Promise<void>;
  refreshLeagues: () => Promise<void>;
}

const EMPTY_LEAGUE_CONTEXT: LeagueContextValue = {
  league: null,
  leagues: [],
  membership: null,
  effectiveMembership: null,
  effectiveRole: 'member',
  viewerIsCommissioner: false,
  isCommissioner: false,
  isOwner: false,
  isOperator: false,
  isMember: false,
  loading: false,
  error: null,
  refreshLeague: async () => {},
  refreshLeagues: async () => {},
};

const LeagueContext = createContext<LeagueContextValue | undefined>(undefined);

export function LeagueProvider({ children }: { children: React.ReactNode }) {
  const params = useParams<{ leagueId?: string }>();
  const { pathname, search } = useLocation();
  const memberPreview = new URLSearchParams(search).get('as') === 'member';
  const leagueId = resolveActiveLeagueId(params, pathname);
  const { user } = useUser();
  const [league, setLeague] = useState<LscLeague | null>(null);
  const [leagues, setLeagues] = useState<LscLeague[]>([]);
  const [membership, setMembership] = useState<LscMember | null>(null);
  const [effectiveMembership, setEffectiveMembership] = useState<LscMember | null>(null);
  const [viewerIsCommissioner, setViewerIsCommissioner] = useState(false);
  const [effectiveRoleFromApi, setEffectiveRoleFromApi] = useState<EffectiveLeagueRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshLeagues = useCallback(async () => {
    if (!user) {
      setLeagues([]);
      return;
    }
    try {
      const items = await listLscLeagues();
      setLeagues(items);
    } catch {
      setLeagues([]);
    }
  }, [user, memberPreview]);

  const refreshLeague = useCallback(async () => {
    if (!leagueId || !user) {
      setLeague(null);
      setMembership(null);
      setEffectiveMembership(null);
      setViewerIsCommissioner(false);
      setEffectiveRoleFromApi(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const detail = await getLscLeague(leagueId);
      const resolvedMembership = detail.membership ?? detail.effectiveMembership ?? null;
      setLeague(detail.league);
      setMembership(resolvedMembership);
      setEffectiveMembership(detail.effectiveMembership ?? resolvedMembership);
      setViewerIsCommissioner(Boolean(detail.viewerIsOperator ?? detail.viewerIsCommissioner));
      setEffectiveRoleFromApi(detail.effectiveRole ?? null);
    } catch (err) {
      const message =
        err instanceof LscApiError ? err.message : 'Could not load league workspace.';
      setError(message);
      setLeague(null);
      setMembership(null);
      setEffectiveMembership(null);
      setViewerIsCommissioner(false);
      setEffectiveRoleFromApi(null);
    } finally {
      setLoading(false);
    }
  }, [leagueId, user, memberPreview]);

  useEffect(() => {
    refreshLeagues();
  }, [refreshLeagues]);

  useEffect(() => {
    refreshLeague();
  }, [refreshLeague]);

  const roleState = useMemo(
    () =>
      computeLeagueRoleState({
        league,
        membership,
        effectiveMembership,
        userUid: user?.uid,
        viewerIsCommissioner,
        effectiveRoleFromApi,
      }),
    [
      league,
      membership,
      effectiveMembership,
      user?.uid,
      viewerIsCommissioner,
      effectiveRoleFromApi,
    ]
  );

  const value = useMemo(
    () => ({
      league,
      leagues,
      membership,
      effectiveMembership,
      effectiveRole: roleState.effectiveRole,
      viewerIsCommissioner: roleState.isOperator,
      // Backward-compatible alias: operator workspace access (owner or commissioner).
      isCommissioner: roleState.isOperator,
      isOwner: roleState.isOwner,
      isOperator: roleState.isOperator,
      isMember: roleState.isMember,
      loading,
      error,
      refreshLeague,
      refreshLeagues,
    }),
    [
      league,
      leagues,
      membership,
      effectiveMembership,
      roleState,
      loading,
      error,
      refreshLeague,
      refreshLeagues,
    ]
  );

  return <LeagueContext.Provider value={value}>{children}</LeagueContext.Provider>;
}

export function useLeague(): LeagueContextValue {
  const ctx = useContext(LeagueContext);
  return ctx ?? EMPTY_LEAGUE_CONTEXT;
}

export function computeLeagueRoleState(opts: {
  league?: LscLeague | null;
  membership?: LscMember | null;
  effectiveMembership?: LscMember | null;
  userUid?: string | null;
  viewerIsCommissioner?: boolean;
  effectiveRoleFromApi?: EffectiveLeagueRole | null;
}) {
  const effectiveRole =
    opts.effectiveRoleFromApi ??
    getEffectiveLeagueRole({
      league: opts.league,
      membership: opts.effectiveMembership ?? opts.membership,
      userUid: opts.userUid,
    });

  const isOwner = isLeagueOwner(effectiveRole);
  const isCommissioner = isLeagueCommissioner(effectiveRole);
  const isOperator = isLeagueOperator(effectiveRole) || Boolean(opts.viewerIsCommissioner);
  const isMember = Boolean(opts.membership || opts.effectiveMembership || isOperator);

  return {
    effectiveRole,
    isOwner,
    isCommissioner,
    isOperator,
    isMember,
  };
}

/** Backward-compatible operator check used by existing tests/components. */
export function computeIsCommissioner(
  opts: {
    membership?: LscMember | null;
    effectiveMembership?: LscMember | null;
    commissionerUid?: string | null;
    userUid?: string | null;
    viewerIsCommissioner?: boolean;
    league?: LscLeague | null;
  } = {}
): boolean {
  return computeLeagueRoleState({
    league: opts.league ?? (opts.commissionerUid ? { commissioner_uid: opts.commissionerUid } as LscLeague : null),
    membership: opts.membership,
    effectiveMembership: opts.effectiveMembership,
    userUid: opts.userUid,
    viewerIsCommissioner: opts.viewerIsCommissioner,
  }).isOperator;
}
